import {
  FormControl,
  TextField,
  Collapse,
  Box,
  Button as MuiButton,
  FormControlLabel,
  Checkbox,
  Typography,
  Grow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FORGOT_PASSWORD, SIGNUP } from 'constants/routes';
import OrDivider from '@buildwise/libs/login/components/OrDivider';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { VerifyUserDomainResponse } from 'hooks/useAuth';
import { LoginFormData } from '../hooks/useLogin';
import RenderAuthOptions from './RenderAuthOptions';
import PasswordInput from './PasswordInput';

type LoginFormProps = {
  verifyDomainResponse: VerifyUserDomainResponse | undefined;
  onSubmit: (values: LoginFormData) => void;
  setVerifyDomainResponse: Dispatch<SetStateAction<VerifyUserDomainResponse | undefined>>;
};

function LoginForm({ verifyDomainResponse, onSubmit, setVerifyDomainResponse }: LoginFormProps) {
  const {
    control,
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    setValue,
  } = useFormContext<LoginFormData>();
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (passwordRef.current && verifyDomainResponse) {
      passwordRef.current.setSelectionRange(-1, -1);
      passwordRef.current.focus();
    }
  }, [passwordRef, verifyDomainResponse]);

  return (
    <Grow in style={{ transformOrigin: 'top center' }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <LoginTitle variant="h3">Log In</LoginTitle>
        <LoginDescription>Please enter your email address.</LoginDescription>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setVerifyDomainResponse(undefined);
                  setValue('username', event.target.value.trim());
                }}
                error={!!fieldState.error}
                helperText={fieldState?.error?.message}
                variant="outlined"
                placeholder="Email"
                autoFocus
                InputLabelProps={{ shrink: true }}
              />
            )}
            name="username"
          />
        </FormControl>

        <Collapse in={!!verifyDomainResponse}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  inputRef={passwordRef}
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message}
                  variant="outlined"
                  placeholder="Password"
                  InputLabelProps={{ shrink: true }}
                  autoComplete="current-password"
                />
              )}
              name="password"
            />
          </FormControl>

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      sx={(theme) => ({
                        padding: 0,
                        marginRight: '6px',
                        '&:not(.Mui-checked)': { color: theme.palette.custom.geyser },
                      })}
                    />
                  )}
                  name="rememberMe"
                />
              }
              label="Remember Me"
            />
            <Link to={FORGOT_PASSWORD} state={{ email: getValues()?.username || '' }}>
              Forgot Password?
            </Link>
          </Box>
        </Collapse>

        <Button fullWidth variant="contained" disabled={isSubmitting} type="submit">
          {isSubmitting && verifyDomainResponse
            ? 'Logging In...'
            : isSubmitting
              ? 'Verifying...'
              : !!verifyDomainResponse
                ? 'Log In'
                : 'Next'}
        </Button>
        <NoAccountText>
          Don’t have an account? <Link to={SIGNUP}>Sign up</Link>
        </NoAccountText>

        <OrDivider />

        <RenderAuthOptions verifyDomainResponse={verifyDomainResponse} />
      </Form>
    </Grow>
  );
}

const LoginTitle = styled(Typography, { label: 'LoginTitle' })(({ theme }) => ({
  fontSize: 36,
  fontWeight: 700,
  color: theme.palette.custom.mirage,
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
    marginBottom: 24,
  },
}));

const LoginDescription = styled(Typography, { label: 'LoginDescription' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.riverBed,
  margin: '12px 0 40px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const Form = styled('form', { label: 'Form' })(({ theme }) => ({
  maxWidth: 500,
  width: '100%',
  padding: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.custom.black,
      },
    },
  },
}));

const Button = styled(MuiButton, { label: 'Button' })({
  borderRadius: 8,
  height: 56,
});

const Link = styled(RouterLink, { label: 'Link' })(({ theme }) => ({
  color: theme.palette.custom.black,
  fontWeight: 600,
  '&:hover, &:focus': {
    textDecoration: 'underline',
  },
}));

const NoAccountText = styled(Typography, { label: 'NoAccountText' })(({ theme }) => ({
  marginTop: 8,
  textAlign: 'center',
  color: theme.palette.custom.label,
}));

export default LoginForm;
